import { useMemo } from 'react';
import type * as Y from 'yjs';
import {
  PLANNED_ITEMS_KEY,
  TRACKERS_DOC,
  TRACKERS_MAP_KEY,
  TRACKER_MAX_PLANNED_ITEMS,
  TRACKER_TAPS_KEY,
  plannedItemSchema,
  trackerDocName,
  trackerMetaSchema,
  trackerTapSchema,
  type PlannedItem,
  type TrackerAction,
  type TrackerCadence,
  type TrackerMeta,
  type TrackerTap,
  type TrackerTemplate,
} from '@awwyeah/shared';
import { useYDoc, useYMap } from '../../sync';

/**
 * The trackers module's Yjs surface. Reads come from the sync hooks' snapshots; **writes go
 * straight to the `Y.Map`** (docs/ARCHITECTURE.md § Sync layer) — no REST ever mutates
 * tracker state, same rule the calendar and lists modules follow.
 *
 * Two docs, mirroring `lists`: the `trackers` registry says which trackers exist, and each
 * `tracker:<id>` doc holds only that tracker's taps. The split is what keeps the Apps tab
 * cheap — an index of six trackers opens six small tap logs, not one ever-growing log of
 * every tap the household has made since the app was installed.
 */

function newId(prefix: string): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${prefix}-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

export interface TrackerRegistry {
  map: Y.Map<TrackerMeta>;
  /** Live, non-archived trackers in a stable order. */
  trackers: TrackerMeta[];
}

export function useTrackerRegistry(): TrackerRegistry {
  const doc = useYDoc(TRACKERS_DOC);
  const { map, entries } = useYMap<TrackerMeta>(doc, TRACKERS_MAP_KEY);

  const trackers = useMemo(
    () =>
      entries
        .map(([, value]) => trackerMetaSchema.safeParse(value))
        .flatMap((parsed) => (parsed.success ? [parsed.data] : []))
        .filter((tracker) => !tracker.archived)
        // Creation order, so a tracker never jumps around the Apps tab when it's tapped.
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id)),
    [entries],
  );

  return { map, trackers };
}

export interface TrackerTapLog {
  map: Y.Map<TrackerTap>;
  taps: TrackerTap[];
}

/**
 * One tracker's taps.
 *
 * Every tap ever made is read, not a recent window: the card needs the newest tap per
 * action (which may be months old for "Bins in"), and the today strip needs today's. A
 * household tapping "fed" twice a day adds ~700 tiny records a year, which is nothing next
 * to a single check-in photo — so no pruning, and no tap is ever silently discarded. If
 * that ever stops being true, prune here and nowhere else.
 */
export function useTrackerTaps(trackerId: string | null): TrackerTapLog {
  // Hooks can't be conditional; with no tracker selected this opens the registry doc,
  // which the DocManager resolves to the same instance, and `taps` below stays empty.
  const doc = useYDoc(trackerId ? trackerDocName(trackerId) : TRACKERS_DOC);
  const { map, entries } = useYMap<TrackerTap>(doc, TRACKER_TAPS_KEY);

  const taps = useMemo(
    () =>
      trackerId
        ? entries
            .map(([, value]) => trackerTapSchema.safeParse(value))
            .flatMap((parsed) => (parsed.success ? [parsed.data] : []))
        : [],
    [entries, trackerId],
  );

  return { map, taps };
}

/** Records a tap. Returns it so the caller can offer an undo without re-reading the doc. */
export function logTap(
  map: Y.Map<TrackerTap>,
  actionId: string,
  byId: string,
  at: Date = new Date(),
): TrackerTap {
  const tap: TrackerTap = {
    id: newId('tap'),
    actionId,
    byId,
    at: at.toISOString(),
  };
  map.set(tap.id, tap);
  return tap;
}

/**
 * Removes a tap — the undo behind the toast, and the only deletion in the module.
 *
 * A mis-tap is the one thing worth erasing: "the dog was fed at 6" is either true or it
 * isn't, and a wrong entry actively misinforms the next person to look, which is the
 * failure this module exists to prevent.
 */
export function removeTap(map: Y.Map<TrackerTap>, tapId: string): void {
  map.delete(tapId);
}

export interface PlannedItemLog {
  map: Y.Map<PlannedItem>;
  /** Earliest first — see `plannedForDay` in `@awwyeah/shared`. */
  items: PlannedItem[];
}

/**
 * A tracker's planned schedule, kept in the same `tracker:<id>` doc as its taps (a sibling
 * `planned` map, `PLANNED_ITEMS_KEY`) so opening a tracker for the day view costs the one
 * extra small doc read, not a second doc subscription.
 */
export function usePlannedItems(trackerId: string | null): PlannedItemLog {
  const doc = useYDoc(trackerId ? trackerDocName(trackerId) : TRACKERS_DOC);
  const { map, entries } = useYMap<PlannedItem>(doc, PLANNED_ITEMS_KEY);

  const items = useMemo(
    () =>
      trackerId
        ? entries
            .map(([, value]) => plannedItemSchema.safeParse(value))
            .flatMap((parsed) => (parsed.success ? [parsed.data] : []))
            .sort((a, b) => a.time.localeCompare(b.time) || a.id.localeCompare(b.id))
        : [],
    [entries, trackerId],
  );

  return { map, items };
}

export type PlannedItemDraft = { label: string; time: string; kind?: string };

/** Adds a planned item. Returns `null` at `TRACKER_MAX_PLANNED_ITEMS` (mirrors `addAction`). */
export function addPlannedItem(
  map: Y.Map<PlannedItem>,
  draft: PlannedItemDraft,
  userId: string,
): PlannedItem | null {
  if (map.size >= TRACKER_MAX_PLANNED_ITEMS) return null;
  const now = new Date().toISOString();
  const item: PlannedItem = {
    id: newId('plan'),
    label: draft.label,
    time: draft.time,
    ...(draft.kind ? { kind: draft.kind } : {}),
    createdBy: userId,
    createdAt: now,
    updatedBy: userId,
    updatedAt: now,
  };
  const parsed = plannedItemSchema.safeParse(item);
  if (!parsed.success) return null;
  map.set(item.id, item);
  return item;
}

export function updatePlannedItem(
  map: Y.Map<PlannedItem>,
  itemId: string,
  patch: Partial<PlannedItemDraft>,
  userId: string,
): PlannedItem | null {
  const existing = plannedItemSchema.safeParse(map.get(itemId));
  if (!existing.success) return null;
  const next: PlannedItem = {
    ...existing.data,
    ...patch,
    id: existing.data.id,
    createdBy: existing.data.createdBy,
    createdAt: existing.data.createdAt,
    updatedBy: userId,
    updatedAt: new Date().toISOString(),
  };
  const parsed = plannedItemSchema.safeParse(next);
  if (!parsed.success) return null;
  map.set(itemId, next);
  return next;
}

/** The only deletion here, same call as `removeTap`/`removeAction`: a plan can just change. */
export function removePlannedItem(map: Y.Map<PlannedItem>, itemId: string): void {
  map.delete(itemId);
}

export function createTrackerFromTemplate(
  map: Y.Map<TrackerMeta>,
  template: TrackerTemplate,
  userId: string,
): TrackerMeta {
  const now = new Date().toISOString();
  const tracker: TrackerMeta = {
    id: newId('trk'),
    name: template.name,
    emoji: template.emoji,
    actions: template.actions.map((action) => ({
      id: newId('act'),
      label: action.label,
      emoji: action.emoji,
      cadence: action.cadence,
    })),
    // The first tracker a household makes is the one they made it for, so it lands on the
    // digest without a second trip through a settings screen.
    pinned: true,
    archived: false,
    createdBy: userId,
    createdAt: now,
    updatedBy: userId,
    updatedAt: now,
  };
  map.set(tracker.id, tracker);
  return tracker;
}

export type TrackerPatch = Partial<Pick<TrackerMeta, 'name' | 'emoji' | 'pinned' | 'archived'>> & {
  actions?: TrackerAction[];
};

export function updateTracker(
  map: Y.Map<TrackerMeta>,
  trackerId: string,
  patch: TrackerPatch,
  userId: string,
): TrackerMeta | null {
  const parsed = trackerMetaSchema.safeParse(map.get(trackerId));
  if (!parsed.success) return null;
  const next: TrackerMeta = {
    ...parsed.data,
    ...patch,
    id: parsed.data.id,
    createdBy: parsed.data.createdBy,
    createdAt: parsed.data.createdAt,
    updatedBy: userId,
    updatedAt: new Date().toISOString(),
  };
  map.set(trackerId, next);
  return next;
}

/** Appends an action. Returns `null` when the tracker is already at `TRACKER_MAX_ACTIONS`. */
export function addAction(
  map: Y.Map<TrackerMeta>,
  trackerId: string,
  action: { label: string; emoji?: string; cadence: TrackerCadence },
  userId: string,
): TrackerMeta | null {
  const parsed = trackerMetaSchema.safeParse(map.get(trackerId));
  if (!parsed.success) return null;
  const next = [
    ...parsed.data.actions,
    {
      id: newId('act'),
      label: action.label,
      ...(action.emoji ? { emoji: action.emoji } : {}),
      cadence: action.cadence,
    },
  ];
  const validated = trackerMetaSchema.safeParse({ ...parsed.data, actions: next });
  if (!validated.success) return null; // over the cap
  return updateTracker(map, trackerId, { actions: next }, userId);
}

/**
 * Drops an action from a tracker. Its taps stay in the log: they record things that
 * genuinely happened, and rewriting history because a button was retired would be the
 * wrong trade. They simply stop being rendered.
 */
export function removeAction(
  map: Y.Map<TrackerMeta>,
  trackerId: string,
  actionId: string,
  userId: string,
): TrackerMeta | null {
  const parsed = trackerMetaSchema.safeParse(map.get(trackerId));
  if (!parsed.success) return null;
  return updateTracker(
    map,
    trackerId,
    { actions: parsed.data.actions.filter((action) => action.id !== actionId) },
    userId,
  );
}
